import type { Me, MktDelta, MktHerkunft, MktKi, MktSeo, MktSeoKeyword, MktTagesauswertung, MktTagesuebersicht, MktTagWert } from '@portal/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { HttpError, marketingApi } from '../../api.js';
import { Problem } from '../ModulePage.js';
import {
  anteil, baum, darfVerwalten, datenstand, deltaAnzeige, deltaProzent, deltas, dimText, euro, gesternBerlin, istMarketingSeite, KI_LEER, kiHatDaten, OHNE_JOURNEY_HINWEIS, positionDelta,
  prozent, QUELLE_STATUS, quelleText, seoPfad, TAG_KACHELN, TAGESAUSWERTUNG_TITEL, tagAusDeutsch, tagDeltas, tagDeutsch, tagKurz, istVorlaeufig, VORLAEUFIG_HINWEIS, zahl, ZEITRAEUME, freierZeitraum, zeitraumQuery, type Knoten, type Zeitraum,
} from './marketing-ui.js';
import { Uebersicht } from './Uebersicht.js';
import './marketing.css';

type Laden<T> = { kind: 'laden' } | { kind: 'fehler'; status: number } | { kind: 'ok'; daten: T };

function useLaden<T>(fn: () => Promise<T>, deps: unknown[]): Laden<T> {
  const [state, setState] = useState<Laden<T>>({ kind: 'laden' });
  useEffect(() => {
    let alive = true;
    setState({ kind: 'laden' });
    fn().then(
      (daten) => alive && setState({ kind: 'ok', daten }),
      (err: unknown) => alive && setState({ kind: 'fehler', status: err instanceof HttpError ? err.status : 0 }),
    );
    return () => {
      alive = false;
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

/**
 * Marketing-Cockpit Stufe 1 (#865c, Konzept v2): P1 Tagesübersicht · P2 Herkunft Sessions · P3 Herkunft Bestellungen/Umsatz ·
 * P4 KI-Assistenten · P5–P7 SEO — seit #901b unterhalb der neuen Übersicht (Uebersicht.tsx) als aufklappbare Detailansicht. Quellen Shopify + SEMrush (Zulieferung n8n → Inbox). Rechte prüft die API (Modul-Gate + Cerbos).
 */
export function Marketing({ me, pathname }: { me: Me; pathname: string }) {
  const [zeitraum, setZeitraum] = useState<Zeitraum>({ preset: '7t' });
  const zq = zeitraumQuery(zeitraum);
  const zk = JSON.stringify(zq);
  const [modell, setModell] = useState<'last' | 'first'>('last');
  const [tag, setTag] = useState(() => gesternBerlin());
  const a = useLaden(() => marketingApi.tagesauswertung(tag), [tag]);
  const t = useLaden(() => marketingApi.tagesuebersicht(zq), [zk]);
  const h = useLaden(() => marketingApi.herkunft({ ...zq, modell }), [zk, modell]);
  const k = useLaden(() => marketingApi.ki(zq), [zk]);
  const s = useLaden(() => marketingApi.seo(), []);
  if (!istMarketingSeite(pathname)) return <Problem status={404} />;
  if (t.kind === 'fehler' && (t.status === 401 || t.status === 403)) return <Problem status={t.status} />;
  return (
    <section className="mod marketing">
      <Uebersicht />
      {/* #901b P23: bisherige Vergleiche (Vorwoche/Vormonat/Vorjahr) unverändert erhalten, nicht als neue Vorperiode umetikettiert */}
      <details className="mku-detailansicht">
        <summary>Detailansicht (bisherige Vergleiche Vorwoche/Vormonat/Vorjahr)</summary>
        <div className="mku-detailansicht__inhalt">
          <div className="marketing-kopf">
            <h2 className="marketing-panel__titel">Marketing · Detailansicht</h2>
            <ZeitraumWahl zeitraum={zeitraum} setZeitraum={setZeitraum} />
          </div>
          <p className="marketing-hinweis">
            Bis gestern bzw. frei Von/Bis; Vergleich mit dem gleich langen Zeitraum eine Woche (VW), vier Wochen (VM) und 52 Wochen (VJ, nur sobald Vorjahresdaten vorliegen) davor. Besucher = Sessions (Shopify). Historie wird nie gelöscht.
            {darfVerwalten(me) && ' Zulieferung: n8n → Inbox (Shopify 05:30, GA4 05:45, Google Ads 06:00 täglich mit 3 Tagen Nachlauf; SEMrush montags 06:00).'}
          </p>
          <div className="marketing-raster">
            <Tagesauswertung stand={a} tag={tag} setTag={setTag} />
            <Panel titel="Tagesübersicht" stand={t} breit>{t.kind === 'ok' && <P1 d={t.daten} />}</Panel>
            <Panel titel="Herkunft Sessions" stand={h} breit>{h.kind === 'ok' && <P2 d={h.daten} />}</Panel>
            <Panel titel="Herkunft Bestellungen & Umsatz" stand={h} breit>{h.kind === 'ok' && <P3 d={h.daten} modell={modell} setModell={setModell} />}</Panel>
            <Panel titel="KI-Assistenten" stand={k}>{k.kind === 'ok' && <P4 d={k.daten} />}</Panel>
            <Panel titel="SEO-Rankings" stand={s} breit>{s.kind === 'ok' && <P5 d={s.daten} />}</Panel>
            <Panel titel="SEO-Chancen (Position 4–20)" stand={s}>{s.kind === 'ok' && <Keywords liste={s.daten.chancen} leer="Noch keine Chancen-Liste." caption="Keywords auf Position 4–20 nach Suchvolumen" />}</Panel>
            <Panel titel="SEO-Wettbewerber" stand={s}>{s.kind === 'ok' && <P7 d={s.daten} />}</Panel>
          </div>
        </div>
      </details>
    </section>
  );
}

function Panel<T extends { datenstand: string | null }>({ titel, stand, breit, children }: { titel: string; stand: Laden<T>; breit?: boolean; children: ReactNode }) {
  const id = `mkt-${titel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  return (
    <section className={`pb-card marketing-panel${breit ? ' marketing-panel--breit' : ''}`} aria-labelledby={id}>
      <h2 id={id} className="marketing-panel__titel">{titel}</h2>
      {stand.kind === 'laden' && <p className="sh-status" role="status">Wird geladen …</p>}
      {stand.kind === 'fehler' && <p className="marketing-fehler" role="alert">{stand.status === 503 ? 'Noch nicht eingerichtet.' : 'Konnte nicht geladen werden.'}</p>}
      {stand.kind === 'ok' && children}
      <p className="marketing-panel__stand">{datenstand(stand.kind === 'ok' ? stand.daten.datenstand : null)}</p>
    </section>
  );
}

/** #895g VJ nur, wenn die API ein Vorjahresfenster mit Daten meldet (vj) — sonst bleibt die Zeile wie bisher */
function Delta({ d, nur, vj }: { d: MktDelta; nur?: 'vorwoche'; vj?: boolean }) {
  const [vw, vm, vjA] = deltas(d);
  return (
    <span className="marketing-delta">
      <DeltaWert a={vw} kurz="VW" />
      {nur !== 'vorwoche' && <DeltaWert a={vm} kurz="VM" />}
      {nur !== 'vorwoche' && vj && <DeltaWert a={vjA} kurz="VJ" />}
    </span>
  );
}

/** #895g Zeitraum: Preset-Schalter (umbrechend) + frei Von/Bis als TT.MM.JJJJ (wie Tagesfeld), Übernahme bei Enter/Verlassen */
function ZeitraumWahl({ zeitraum, setZeitraum }: { zeitraum: Zeitraum; setZeitraum: (z: Zeitraum) => void }) {
  const frei = 'von' in zeitraum;
  const [von, setVon] = useState(frei ? tagDeutsch(zeitraum.von) : '');
  const [bis, setBis] = useState(frei ? tagDeutsch(zeitraum.bis) : tagDeutsch(gesternBerlin()));
  const [fehler, setFehler] = useState<string | null>(null);
  const uebernehmen = () => {
    if (!von.trim()) return setFehler(null);
    const z = freierZeitraum(von, bis, gesternBerlin());
    if (typeof z === 'string') return setFehler(z);
    setFehler(null);
    if (!frei || zeitraum.von !== (z as { von: string }).von || zeitraum.bis !== (z as { bis: string }).bis) setZeitraum(z);
  };
  const feld = (label: string, wert: string, setWert: (v: string) => void) => (
    <label className="marketing-tagwahl">
      <span className="marketing-meta">{label}</span>
      <input type="text" inputMode="numeric" lang="de" autoComplete="off" placeholder="TT.MM.JJJJ" maxLength={10} className="marketing-tagwahl__eingabe"
        value={wert} aria-invalid={fehler ? true : undefined} aria-describedby={fehler ? 'mkt-zeitraum-fehler' : undefined}
        onChange={(e) => setWert(e.target.value)} onBlur={uebernehmen} onKeyDown={(e) => { if (e.key === 'Enter') uebernehmen(); }} />
    </label>
  );
  return (
    <div className="marketing-zeitraum">
      <div className="marketing-zeitraum" role="group" aria-label="Zeitraum">
        {ZEITRAEUME.map((z) => (
          <button key={z.preset} type="button" className="marketing-schalter" aria-pressed={!frei && zeitraum.preset === z.preset} onClick={() => { setFehler(null); setZeitraum({ preset: z.preset }); }}>
            {z.label}
          </button>
        ))}
      </div>
      <div className="marketing-tagwahl" role="group" aria-label="Zeitraum frei">
        {feld('Von', von, setVon)}
        {feld('Bis', bis, setBis)}
      </div>
      {fehler && <span id="mkt-zeitraum-fehler" className="marketing-fehler marketing-meta" role="alert">{fehler}</span>}
    </div>
  );
}
function DeltaWert({ a, kurz }: { a: ReturnType<typeof deltaAnzeige>; kurz: string }) {
  return (
    <span className={`marketing-delta__wert marketing-delta__wert--${a.richtung}`}>
      <span aria-hidden="true">{kurz} {a.richtung === 'hoch' ? '▲' : a.richtung === 'runter' ? '▼' : ''} {a.text}</span>
      <span className="sr-only">{a.sr}</span>
    </span>
  );
}
const Leer = ({ text }: { text: string }) => <div className="marketing-leer"><p className="marketing-leer__text">{text}</p></div>;

/** #895 Tagesauswertung eines Tages (Standard gestern): Kacheln mit Δ Vortag/Vorwoche/Vormonat, Top-5-Herkunft, Quellen-Leiste */
function Tagesauswertung({ stand, tag, setTag }: { stand: Laden<MktTagesauswertung>; tag: string; setTag: (t: string) => void }) {
  const id = 'mkt-tagesauswertung';
  const max = gesternBerlin();
  return (
    <section className="pb-card marketing-panel marketing-panel--breit" aria-labelledby={id}>
      <div className="marketing-kopf">
        <h2 id={id} className="marketing-panel__titel">{TAGESAUSWERTUNG_TITEL}</h2>
        <TagWahl tag={tag} max={max} setTag={setTag} />
      </div>
      {stand.kind === 'laden' && <p className="sh-status" role="status">Wird geladen …</p>}
      {stand.kind === 'fehler' && <p className="marketing-fehler" role="alert">{stand.status === 503 ? 'Noch nicht eingerichtet.' : stand.status === 400 ? 'Ungültiger Tag.' : 'Konnte nicht geladen werden.'}</p>}
      {stand.kind === 'ok' && <TagesauswertungInhalt d={stand.daten} />}
    </section>
  );
}

/** #895d Tag-Eingabe immer TT.MM.JJJJ (Textfeld statt type=date → unabhängig von der Browser-Sprache); Übernahme bei Enter/Verlassen */
function TagWahl({ tag, max, setTag }: { tag: string; max: string; setTag: (t: string) => void }) {
  const [text, setText] = useState(tagDeutsch(tag));
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { setText(tagDeutsch(tag)); setFehler(null); }, [tag]);
  const uebernehmen = () => {
    const iso = tagAusDeutsch(text);
    if (!iso) return setFehler('Bitte als TT.MM.JJJJ eingeben.');
    if (iso > max) return setFehler(`Spätestens ${tagDeutsch(max)} (gestern).`);
    setFehler(null);
    if (iso !== tag) setTag(iso);
  };
  return (
    <div className="marketing-tagwahl">
      <label className="marketing-tagwahl">
        <span className="marketing-meta">Tag</span>
        <input type="text" inputMode="numeric" lang="de" autoComplete="off" placeholder="TT.MM.JJJJ" maxLength={10} className="marketing-tagwahl__eingabe"
          value={text} aria-invalid={fehler ? true : undefined} aria-describedby={fehler ? 'mkt-tagwahl-fehler' : undefined}
          onChange={(e) => setText(e.target.value)} onBlur={uebernehmen} onKeyDown={(e) => { if (e.key === 'Enter') uebernehmen(); }} />
      </label>
      {fehler && <span id="mkt-tagwahl-fehler" className="marketing-fehler marketing-meta" role="alert">{fehler}</span>}
    </div>
  );
}

function TagDelta({ w, vj }: { w: MktTagWert; vj?: boolean }) {
  const [vt, vw, vm, vjA] = tagDeltas(w);
  return (
    <span className="marketing-delta">
      <DeltaWert a={vt} kurz="VT" />
      <DeltaWert a={vw} kurz="VW" />
      <DeltaWert a={vm} kurz="VM" />
      {vj && <DeltaWert a={vjA} kurz="VJ" />}
    </span>
  );
}

function TagesauswertungInhalt({ d }: { d: MktTagesauswertung }) {
  const paar = TAG_KACHELN.filter((x) => x.paar);
  const rest = TAG_KACHELN.filter((x) => !x.paar);
  const kachel = (x: (typeof TAG_KACHELN)[number]) => (
    <li key={x.key} className="marketing-kachel">
      <p className="marketing-kachel__label">{x.label}</p>
      <p className="marketing-kachel__wert">{x.format(d.kennzahlen[x.key].wert)}</p>
      <TagDelta w={d.kennzahlen[x.key]} vj={d.vergleich.vorjahr !== null} />
      {x.zusatz?.map((z) => (
        <p key={z.key} className="marketing-meta">{z.label} {z.format(d.kennzahlen[z.key].wert)}</p>
      ))}
    </li>
  );
  return (
    <>
      <p className="marketing-meta">
        {tagKurz(d.tag)}
        {istVorlaeufig(d.tag) && <> <span className="marketing-vorlaeufig" title={VORLAEUFIG_HINWEIS}>vorläufig<span className="sr-only"> – {VORLAEUFIG_HINWEIS}</span></span></>}
        {' '}· Vergleich Vortag (VT) {tagKurz(d.vergleich.vortag)}, Vorwoche (VW) {tagKurz(d.vergleich.vorwoche)}, Vormonat (VM) {tagKurz(d.vergleich.vormonat)}{d.vergleich.vorjahr && <>, Vorjahr (VJ) {tagKurz(d.vergleich.vorjahr)}</>}. „—“ = Quelle hat für den Tag nichts geliefert.
      </p>
      <ul className="marketing-kacheln marketing-kacheln--paar" aria-label="Sessions Shopify und GA4">{paar.map(kachel)}</ul>
      <ul className="marketing-kacheln">{rest.map(kachel)}</ul>
      <div className="marketing-tagesauswertung__unten">
        <div>
          <h3 className="marketing-meta">Top-5-Herkunft (Sessions Shopify)</h3>
          {d.topHerkunft.length === 0 ? <Leer text="Noch keine Herkunftsdaten für diesen Tag." /> : (
            <ol className="marketing-liste">
              {d.topHerkunft.map((h) => (
                <li key={`${h.kanalgruppe}/${h.referrerName}`} className="marketing-liste__zeile">
                  <span>{dimText(h.kanalgruppe, '(ohne Kanal)')} · {dimText(h.referrerName, '(ohne Referrer)')}</span>
                  <span className="marketing-baum__wert">{zahl(h.sessions)} ({anteil(h.anteil)})</span>
                  <span className="marketing-delta"><DeltaWert a={deltaAnzeige(h.deltaVorwoche, 'Vorwoche')} kurz="VW" /></span>
                </li>
              ))}
            </ol>
          )}
          <p className="marketing-meta">KI-Assistenten: {d.ki.assistenten.filter((x) => x.sessions > 0).map((x) => `${x.name} ${zahl(x.sessions)}`).join(' · ') || 'keine Sessions'} (Untergrenze)</p>
        </div>
        <div>
          <h3 className="marketing-meta">Quellen</h3>
          <ul className="marketing-quellen">
            {d.quellen.map((q) => {
              const st = QUELLE_STATUS[q.status];
              return (
                <li key={q.system} className="marketing-quelle">
                  <span className="marketing-quelle__name">{q.name}</span>
                  <span className={`marketing-badge marketing-badge--${st.ton}`}>{st.text}</span>
                  <span className="marketing-meta marketing-quelle__text">{quelleText(q)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
}

function P1({ d }: { d: MktTagesuebersicht }) {
  const kacheln = [
    { label: 'Sessions', wert: zahl(d.summe.sessions), delta: d.delta.sessions },
    { label: 'Bestellungen', wert: zahl(d.summe.orders), delta: d.delta.orders },
    { label: 'Umsatz', wert: euro(d.summe.umsatz), delta: d.delta.umsatz },
    { label: 'Neue Kunden', wert: zahl(d.summe.neueKunden), delta: d.delta.neueKunden },
    { label: 'Conversion', wert: prozent(d.summe.conversion), delta: d.delta.conversion },
    { label: 'Ø Bestellwert', wert: euro(d.summe.warenkorb), delta: d.delta.warenkorb },
  ];
  if (!d.datenstand && d.tage.every((t) => t.sessions === null && t.orders === null)) return <Leer text="Noch keine Daten aus Shopify." />;
  const max = Math.max(1, ...d.tage.map((t) => Math.max(t.sessions ?? 0, t.sessionsVorwoche ?? 0)));
  return (
    <>
      <ul className="marketing-kacheln">
        {kacheln.map((x) => (
          <li key={x.label} className="marketing-kachel">
            <p className="marketing-kachel__label">{x.label}</p>
            <p className="marketing-kachel__wert">{x.wert}</p>
            <Delta d={x.delta} vj={d.vergleich.vorjahr !== null} />
          </li>
        ))}
      </ul>
      {/* Grafik ist redundant zur Tabelle (Sessions + Vorwoche je Tag) und daher für Hilfstechnik ausgeblendet (Review 229) */}
      <figure className="marketing-trend" aria-hidden="true">
        {d.tage.map((t) => (
          <div key={t.tag} className="marketing-trend__tag" title={`${tagKurz(t.tag)}: ${zahl(t.sessions)} Sessions (Vorwoche ${zahl(t.sessionsVorwoche)})`}>
            <span className="marketing-trend__balken marketing-trend__balken--vw" style={{ height: `${((t.sessionsVorwoche ?? 0) / max) * 100}%` }} />
            <span className="marketing-trend__balken" style={{ height: `${((t.sessions ?? 0) / max) * 100}%` }} />
          </div>
        ))}
        <figcaption className="marketing-legende"><span className="marketing-legende__ist">Sessions</span> <span className="marketing-legende__vw">Vorwoche</span></figcaption>
      </figure>
      <div className="marketing-scroll">
        <table className="marketing-tabelle">
          <caption className="sr-only">Kennzahlen je Tag, Sessions mit gleichem Wochentag der Vorwoche</caption>
          <thead><tr><th scope="col">Tag</th><th scope="col">Sessions</th><th scope="col">Sessions Vorwoche</th><th scope="col">Bestellungen</th><th scope="col">Umsatz</th><th scope="col">Neue Kunden</th></tr></thead>
          <tbody>
            {[...d.tage].reverse().map((t) => (
              <tr key={t.tag}>
                <th scope="row" data-label="Tag">{tagKurz(t.tag)}</th>
                <td data-label="Sessions">{zahl(t.sessions)}</td>
                <td data-label="Sessions Vorwoche">{zahl(t.sessionsVorwoche)}</td>
                <td data-label="Bestellungen">{zahl(t.orders)}</td>
                <td data-label="Umsatz">{euro(t.umsatz)}</td>
                <td data-label="Neue Kunden">{zahl(t.neueKunden)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Baum({ knoten, format, tiefe = 0, vj }: { knoten: Knoten[]; format: (v: number) => string; tiefe?: number; vj?: boolean }) {
  return (
    <ul className={`marketing-baum${tiefe === 0 ? ' marketing-baum--wurzel' : ''}`}>
      {knoten.map((k) => {
        const zeile = (
          <span className="marketing-baum__zeile">
            <span className="marketing-baum__label">{k.label}</span>
            <span className="marketing-baum__wert">{format(k.wert)}</span>
            <Delta d={{ vorwoche: deltaProzent(k.wert, k.vorwoche), vormonat: deltaProzent(k.wert, k.vormonat), vorjahr: vj ? deltaProzent(k.wert, k.vorjahr) : null }} vj={vj} />
          </span>
        );
        return (
          <li key={k.schluessel}>
            {k.kinder.length ? (
              <details>
                <summary>{zeile}</summary>
                <Baum knoten={k.kinder} format={format} tiefe={tiefe + 1} vj={vj} />
              </details>
            ) : (
              <div className="marketing-baum__blatt">{zeile}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function P2({ d }: { d: MktHerkunft }) {
  if (!d.sessions.zeilen.length) return <Leer text="Noch keine Herkunftsdaten." />;
  const b = baum(d.sessions.zeilen, (z) => z.sessions, (z) => z.vorwoche, (z) => z.vormonat, (z) => z.vorjahr);
  return (
    <>
      <p className="marketing-meta">Kanalgruppe → Referrer → UTM source / medium → Kampagne · Inhalt. {zahl(d.sessions.summe)} Sessions; aufklappen für Details.</p>
      <ul className="marketing-anteile" aria-label="Anteile je Kanalgruppe">
        {b.map((k) => (
          <li key={k.schluessel} className="marketing-anteil">
            <span className="marketing-anteil__label">{k.label}</span>
            <span className="marketing-anteil__balken" aria-hidden="true"><span style={{ width: `${(k.wert / Math.max(1, d.sessions.summe)) * 100}%` }} /></span>
            <span className="marketing-anteil__wert">{anteil(k.wert / Math.max(1, d.sessions.summe))}</span>
          </li>
        ))}
      </ul>
      <Baum knoten={b} format={zahl} vj={d.vergleich.vorjahr !== null} />
    </>
  );
}

function P3({ d, modell, setModell }: { d: MktHerkunft; modell: 'last' | 'first'; setModell: (m: 'last' | 'first') => void }) {
  const o = d.orders;
  return (
    <>
      <div className="marketing-zeitraum" role="group" aria-label="Zuordnung">
        <button type="button" className="marketing-schalter" aria-pressed={modell === 'last'} onClick={() => setModell('last')}>Letzter Besuch</button>
        <button type="button" className="marketing-schalter" aria-pressed={modell === 'first'} onClick={() => setModell('first')}>Erster Besuch</button>
      </div>
      <p className="marketing-untergrenze" role="note">{OHNE_JOURNEY_HINWEIS}</p>
      {o.summe.orders === 0 ? <Leer text="Noch keine Bestellungen mit Herkunft." /> : (
        <>
          <p className="marketing-quote">
            <strong>Zugeordnet: {anteil(o.attributionsquote)} der Bestellungen</strong> ({zahl(o.summe.orders - o.ohneJourney.orders)} von {zahl(o.summe.orders)}).
          </p>
          <Baum knoten={baum(o.zeilen, (z) => z.umsatz, (z) => z.vorwoche.umsatz, (z) => z.vormonat.umsatz, (z) => z.vorjahr.umsatz)} format={euro} vj={d.vergleich.vorjahr !== null} />
          <div className="marketing-baum__blatt marketing-ohne">
            <span className="marketing-baum__zeile">
              <span className="marketing-baum__label">Ohne Journey (nicht zuordenbar)</span>
              <span className="marketing-baum__wert">{euro(o.ohneJourney.umsatz)} · {zahl(o.ohneJourney.orders)} Bestellungen</span>
            </span>
          </div>
        </>
      )}
    </>
  );
}

function P4({ d }: { d: MktKi }) {
  return (
    <>
      <p className="marketing-untergrenze" role="note">
        <strong>Untergrenze:</strong> KI-Apps ohne Referrer/UTM zählen als „Direkt“; Abrufe von KI-Crawlern sind auf Shopify-Hosting nicht messbar.
      </p>
      {!kiHatDaten(d) ? <Leer text={KI_LEER} /> : (<>
      <p className="marketing-meta">{zahl(d.summe.sessions)} Sessions ({anteil(d.anteilSessions)} aller) · {zahl(d.summe.orders)} Bestellungen · {euro(d.summe.umsatz)}</p>
      <div className="marketing-scroll-x">
      <table className="marketing-tabelle marketing-tabelle--ki">
        <caption className="sr-only">KI-Assistenten</caption>
        <thead><tr><th scope="col">Assistent</th><th scope="col">Sessions</th><th scope="col">Bestellungen</th><th scope="col">Umsatz</th><th scope="col">Δ Sessions</th></tr></thead>
        <tbody>
          {d.assistenten.map((a) => (
            <tr key={a.name}>
              <th scope="row" data-label="Assistent">{a.name}</th>
              <td data-label="Sessions">{zahl(a.sessions)}</td>
              <td data-label="Bestellungen">{zahl(a.orders)}</td>
              <td data-label="Umsatz">{euro(a.umsatz)}</td>
              <td data-label="Δ Sessions"><Delta d={a.delta} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      </>)}
    </>
  );
}

function Keywords({ liste, leer, caption }: { liste: MktSeoKeyword[]; leer: string; caption: string }) {
  if (!liste.length) return <Leer text={leer} />;
  return (
    <div className="marketing-scroll">
      <table className="marketing-tabelle">
        <caption className="sr-only">{caption}</caption>
        <thead><tr><th scope="col">Keyword</th><th scope="col">Position</th><th scope="col">Änderung</th><th scope="col">Suchvolumen</th><th scope="col">URL</th></tr></thead>
        <tbody>
          {liste.map((k) => {
            const p = positionDelta(k.delta, k.status);
            return (
              <tr key={k.keyword}>
                <th scope="row" data-label="Keyword">{k.keyword}</th>
                <td data-label="Position">{k.position}</td>
                <td data-label="Änderung"><span className={`marketing-delta__wert marketing-delta__wert--${p.richtung}`}><span aria-hidden="true">{p.text}</span><span className="sr-only">{p.sr}</span></span></td>
                <td data-label="Suchvolumen">{zahl(k.suchvolumen)}</td>
                <td data-label="URL" className="marketing-url">{seoPfad(k.url)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function P5({ d }: { d: MktSeo }) {
  if (!d.woche) return <Leer text="Noch kein SEMrush-Snapshot." />;
  return (
    <>
      <p className="marketing-meta">
        Woche ab {tagKurz(d.woche)}{d.vergleichWoche ? `, verglichen mit Woche ab ${tagKurz(d.vergleichWoche)}` : ' (noch kein Snapshot vor 4 Wochen — Vorposition aus SEMrush)'} ·
        {' '}{d.zaehler.besser} besser · {d.zaehler.schlechter} schlechter · {d.zaehler.gleich} gleich · {d.zaehler.neu} neu
        {d.verloren.length > 0 && ` · ${d.verloren.length} verloren`}
      </p>
      <Keywords liste={d.top} leer="Keine Top-Keywords." caption="Top-Keywords nach Traffic" />
      {d.units && <p className="marketing-meta">SEMrush-Units {d.units.monat}: {zahl(d.units.verbraucht)} von {zahl(d.units.budget)} verbraucht.</p>}
    </>
  );
}

function P7({ d }: { d: MktSeo }) {
  if (!d.wettbewerber.length) return <Leer text="Noch keine Wettbewerber-Daten." />;
  return (
    <table className="marketing-tabelle">
      <caption className="sr-only">Wettbewerber nach Relevanz</caption>
      <thead><tr><th scope="col">Domain</th><th scope="col">Relevanz</th><th scope="col">Gem. Keywords</th><th scope="col">Δ 4 Wochen</th></tr></thead>
      <tbody>
        {d.wettbewerber.map((w) => (
          <tr key={w.domain}>
            <th scope="row" data-label="Domain">{w.domain}</th>
            <td data-label="Relevanz">{w.relevanz === null ? '—' : w.relevanz.toLocaleString('de-DE', { maximumFractionDigits: 2 })}</td>
            <td data-label="Gem. Keywords">{zahl(w.gemeinsameKeywords)}</td>
            <td data-label="Δ 4 Wochen">{w.deltaGemeinsame === null ? '—' : `${w.deltaGemeinsame > 0 ? '+' : ''}${zahl(w.deltaGemeinsame)}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
